import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import vaultsourcegroupService from '../services/vaultsourcegroupService';
import vaultsourceService from '../services/vaultsourceService';
import vaultsourcegroupvaulttableService from '../services/vaultsourcegroupvaulttableService';
import vaulttableService from '../services/vaulttableService';
import vaulttablecolumnService from '../services/vaulttablecolumnService';
import vaultsqlService from '../services/vaultsqlService';
import vaultsqlparametersService from '../services/vaultsqlparametersService';
import canonicaltemplateService from '../services/canonicaltemplateService';
import './VaultSourceGroupForm.css';

function VaultSourceGroupForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id, vaultsourceid } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vaultSourceName, setVaultSourceName] = useState('');
  const [associatedVaultTables, setAssociatedVaultTables] = useState([]);
  const [availableVaultTables, setAvailableVaultTables] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const [savingTables, setSavingTables] = useState(false);
  const [activeTab, setActiveTab] = useState('vaulttables');
  const [vaultSqls, setVaultSqls] = useState([]);
  const [showSqlForm, setShowSqlForm] = useState(false);
  const [editingSqlId, setEditingSqlId] = useState(null);
  const [sqlFormData, setSqlFormData] = useState({
    name: '',
    sql_source: '',
    canonical_template_id: ''
  });
  const [allTemplates, setAllTemplates] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [showParameterForm, setShowParameterForm] = useState(false);
  const [editingParameterId, setEditingParameterId] = useState(null);
  const [parameterFormData, setParameterFormData] = useState({
    name: '',
    value: '',
    description: '',
    data_type: ''
  });
  const [tablesWithColumns, setTablesWithColumns] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [selectedColumnId, setSelectedColumnId] = useState('');
  const [formData, setFormData] = useState({
    vaultsourceid: '',
    name: '',
    description: '',
    icon: '',
    data_type_key: '',
    sql_source: ''
  });

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  useEffect(() => {
    // Auto-populate vaultsourceid if creating from vault source page
    if (vaultsourceid && isCreateMode) {
      console.log('Setting vaultsourceid from params:', vaultsourceid);
      setFormData(prev => ({ ...prev, vaultsourceid: parseInt(vaultsourceid) }));
    }
  }, [vaultsourceid, isCreateMode]);

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      fetchVaultSourceGroup();
      fetchAssociatedVaultTables();
      fetchVaultSqls();
    }
  }, [id, isEditMode, isViewMode]);

  useEffect(() => {
    // Fetch available vault system tables when vaultsourceid is set
    if (formData.vaultsourceid || vaultsourceid) {
      fetchAvailableVaultTables(formData.vaultsourceid || vaultsourceid);
    }
  }, [formData.vaultsourceid, vaultsourceid]);

  useEffect(() => {
    // Fetch vault source when vaultsourceid is set
    if (formData.vaultsourceid || vaultsourceid) {
      fetchVaultSource(formData.vaultsourceid || vaultsourceid);
    }
  }, [formData.vaultsourceid, vaultsourceid]);

  const fetchVaultSource = async (vaultsourceid) => {
    try {
      const vaultsourceData = await vaultsourceService.getVaultSourceById(vaultsourceid);
      setVaultSourceName(vaultsourceData.name || '');
    } catch (err) {
      console.error('Failed to fetch vault source:', err);
    }
  };

  const fetchVaultSourceGroup = async () => {
    try {
      setLoading(true);
      const vaultsourcegroupData = await vaultsourcegroupService.getVaultSourceGroupById(id);
      setFormData(vaultsourcegroupData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch vault source group');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssociatedVaultTables = async () => {
    try {
      const response = await vaultsourcegroupvaulttableService.getVaultTablesForGroup(id);
      setAssociatedVaultTables(response.vaulttables || []);
    } catch (err) {
      console.error('Failed to fetch associated vault system tables:', err);
    }
  };

  const fetchAvailableVaultTables = async (vaultsourceid) => {
    try {
      const response = await vaulttableService.getAllVaultTables({ vaultsourceid });
      setAvailableVaultTables(response.vaulttables || []);
    } catch (err) {
      console.error('Failed to fetch available vault system tables:', err);
    }
  };

  const handleToggleTableSelection = (tableId) => {
    setSelectedTableIds(prev => {
      if (prev.includes(tableId)) {
        return prev.filter(id => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  };

  const handleAddSelectedTables = async () => {
    if (selectedTableIds.length === 0) {
      setError('Please select at least one vault table');
      return;
    }

    try {
      setSavingTables(true);
      setError(null);
      await vaultsourcegroupvaulttableService.addVaultTablesToGroup(id, selectedTableIds);

      // Refresh associated tables and clear selection
      await fetchAssociatedVaultTables();
      setSelectedTableIds([]);
      setSearchTerm('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add vault system tables');
    } finally {
      setSavingTables(false);
    }
  };

  const handleRemoveVaultTable = async (junctionId) => {
    if (!window.confirm('Are you sure you want to remove this vault table from the group?')) {
      return;
    }

    try {
      await vaultsourcegroupvaulttableService.removeVaultTableFromGroup(junctionId);
      await fetchAssociatedVaultTables();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove vault table');
    }
  };

  const fetchVaultSqls = async () => {
    try {
      const response = await vaultsqlService.getAllVaultSqls({ vaultsourcegroupid: id });
      setVaultSqls(response.vaultsqls || []);
    } catch (err) {
      console.error('Failed to fetch vault SQLs:', err);
    }
  };

  const fetchAllTemplates = async () => {
    try {
      const response = await canonicaltemplateService.getAll();
      setAllTemplates(response.data.canonicaltemplates || []);
    } catch (err) {
      console.error('Failed to fetch canonical templates:', err);
    }
  };

  const handleCreateSql = () => {
    setSqlFormData({ name: '', sql_source: '', canonical_template_id: '' });
    setEditingSqlId(null);
    setShowSqlForm(true);
    fetchAllTemplates();
    setParameters([]);
    setShowParameterForm(false);
    setParameterFormData({ name: '', value: '', description: '', data_type: '' });
    setSelectedTableId('');
    setSelectedColumnId('');
    setTablesWithColumns([]);
  };

  const handleEditSql = async (vaultsql) => {
    setSqlFormData({
      name: vaultsql.name,
      sql_source: vaultsql.sql_source || '',
      canonical_template_id: vaultsql.canonical_template_id || ''
    });
    setEditingSqlId(vaultsql.id);
    setShowSqlForm(true);
    fetchAllTemplates();
    await fetchParameters(vaultsql.id);
  };

  const fetchParameters = async (vaultsqlid) => {
    try {
      const response = await vaultsqlparametersService.getAllVaultSqlParameters({ vaultsqlid });
      setParameters(response.vaultsqlparameters || []);
    } catch (err) {
      console.error('Failed to fetch parameters:', err);
      setParameters([]);
    }
  };

  const fetchTablesWithColumns = async () => {
    try {
      // Get tables for this group
      const tablesResponse = await vaultsourcegroupvaulttableService.getVaultTablesForGroup(id);
      const tables = tablesResponse.vaulttables || [];

      // Fetch columns for each table
      const tablesWithCols = await Promise.all(
        tables.map(async (table) => {
          try {
            const columnsResponse = await vaulttablecolumnService.getAllVaultTableColumns({
              vaulttableid: table.id
            });
            return {
              ...table,
              columns: columnsResponse.vaulttablecolumns || []
            };
          } catch (err) {
            console.error(`Failed to fetch columns for table ${table.id}:`, err);
            return {
              ...table,
              columns: []
            };
          }
        })
      );

      setTablesWithColumns(tablesWithCols);
    } catch (err) {
      console.error('Failed to fetch tables with columns:', err);
      setTablesWithColumns([]);
    }
  };

  const handleAddParameter = async () => {
    setError(null);

    // If SQL hasn't been saved yet, save it first
    if (!editingSqlId) {
      if (!sqlFormData.name) {
        setError('Please enter a name for the SQL query before adding parameters');
        return;
      }

      try {
        const response = await vaultsqlService.createVaultSql({
          ...sqlFormData,
          vaultsourcegroupid: id
        });
        setEditingSqlId(response.id);
        await fetchVaultSqls();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to save vault SQL');
        return;
      }
    }

    // Fetch tables and columns
    await fetchTablesWithColumns();

    setParameterFormData({ name: '', value: '', description: '', data_type: '' });
    setSelectedTableId('');
    setSelectedColumnId('');
    setEditingParameterId(null);
    setShowParameterForm(true);
  };

  const handleEditParameter = async (parameter) => {
    // Fetch tables and columns
    await fetchTablesWithColumns();

    setParameterFormData({
      name: parameter.name,
      value: parameter.value || '',
      description: parameter.description || '',
      data_type: parameter.data_type || ''
    });
    setSelectedTableId('');
    setSelectedColumnId('');
    setEditingParameterId(parameter.id);
    setShowParameterForm(true);
  };

  const handleParameterFormChange = (e) => {
    const { name, value } = e.target;
    setParameterFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTableSelect = (e) => {
    const tableId = e.target.value;
    setSelectedTableId(tableId);
    setSelectedColumnId('');
    // Clear parameter name when table changes
    setParameterFormData(prev => ({
      ...prev,
      name: ''
    }));
  };

  const handleColumnSelect = (e) => {
    const columnId = e.target.value;
    setSelectedColumnId(columnId);

    if (columnId && selectedTableId) {
      const table = tablesWithColumns.find(t => t.id === parseInt(selectedTableId));
      const column = table?.columns.find(c => c.id === parseInt(columnId));

      if (table && column) {
        // Set parameter name as table.column
        setParameterFormData(prev => ({
          ...prev,
          name: `${table.name}.${column.name}`
        }));
      }
    }
  };

  const handleSaveParameter = async (e) => {
    e.preventDefault();
    setError(null);

    if (!parameterFormData.name) {
      setError('Parameter name is required');
      return;
    }

    if (!editingSqlId) {
      setError('Please save the SQL first before adding parameters');
      return;
    }

    try {
      if (editingParameterId) {
        await vaultsqlparametersService.updateVaultSqlParameter(editingParameterId, {
          ...parameterFormData,
          vaultsqlid: editingSqlId
        });
      } else {
        await vaultsqlparametersService.createVaultSqlParameter({
          ...parameterFormData,
          vaultsqlid: editingSqlId
        });
      }
      await fetchParameters(editingSqlId);
      setShowParameterForm(false);
      setParameterFormData({ name: '', value: '', description: '', data_type: '' });
      setEditingParameterId(null);
      setSelectedTableId('');
      setSelectedColumnId('');
      setTablesWithColumns([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save parameter');
    }
  };

  const handleCancelParameterForm = () => {
    setShowParameterForm(false);
    setParameterFormData({ name: '', value: '', description: '', data_type: '' });
    setEditingParameterId(null);
    setSelectedTableId('');
    setSelectedColumnId('');
    setTablesWithColumns([]);
  };

  const handleDeleteParameter = async (parameterId) => {
    if (!window.confirm('Are you sure you want to delete this parameter?')) {
      return;
    }

    try {
      await vaultsqlparametersService.deleteVaultSqlParameter(parameterId);
      await fetchParameters(editingSqlId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete parameter');
    }
  };

  const handleSqlFormChange = (e) => {
    const { name, value } = e.target;
    setSqlFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveSql = async (e) => {
    e.preventDefault();
    setError(null);

    if (!sqlFormData.name) {
      setError('Name is required');
      return;
    }

    try {
      const wasUpdating = !!editingSqlId;

      if (editingSqlId) {
        await vaultsqlService.updateVaultSql(editingSqlId, {
          ...sqlFormData,
          vaultsourcegroupid: id
        });
      } else {
        const response = await vaultsqlService.createVaultSql({
          ...sqlFormData,
          vaultsourcegroupid: id
        });
        setEditingSqlId(response.id);
      }

      await fetchVaultSqls();

      // Close the form if we were updating existing SQL
      if (wasUpdating) {
        setShowSqlForm(false);
        setSqlFormData({ name: '', sql_source: '' });
        setEditingSqlId(null);
        setParameters([]);
        setShowParameterForm(false);
      }
      // If we just created new SQL, keep form open so user can add parameters
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save vault SQL');
    }
  };

  const handleCancelSqlForm = () => {
    setShowSqlForm(false);
    setSqlFormData({ name: '', sql_source: '', canonical_template_id: '' });
    setEditingSqlId(null);
    setError(null);
    setParameters([]);
    setShowParameterForm(false);
    setParameterFormData({ name: '', value: '', description: '', data_type: '' });
    setSelectedTableId('');
    setSelectedColumnId('');
    setTablesWithColumns([]);
  };

  const handleDeleteSql = async (vaultsqlId) => {
    if (!window.confirm('Are you sure you want to delete this vault SQL?')) {
      return;
    }

    try {
      await vaultsqlService.deleteVaultSql(vaultsqlId);
      await fetchVaultSqls();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vault SQL');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate required fields
    if (!formData.vaultsourceid) {
      setError('Vault System ID is required');
      setLoading(false);
      return;
    }

    if (!formData.name) {
      setError('Name is required');
      setLoading(false);
      return;
    }

    try {
      console.log('Submitting vault source group data:', formData);

      if (isCreateMode) {
        const response = await vaultsourcegroupService.createVaultSourceGroup(formData);
        console.log('Vault source group created:', response);
      } else if (isEditMode) {
        const response = await vaultsourcegroupService.updateVaultSourceGroup(id, formData);
        console.log('Vault source group updated:', response);
      }

      // Navigate back to vault source view
      const targetVaultSourceId = vaultsourceid || formData.vaultsourceid;
      if (targetVaultSourceId) {
        navigate(`/admin/vaults/${targetVaultSourceId}`);
      } else {
        navigate('/admin/vaults');
      }
    } catch (err) {
      console.error('Error saving vault source group:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save vault source group');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const targetVaultSourceId = vaultsourceid || formData.vaultsourceid;
    if (targetVaultSourceId) {
      navigate(`/admin/vaults/${targetVaultSourceId}`);
    } else {
      navigate('/admin/vaults');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this vault source group?')) {
      return;
    }

    try {
      setLoading(true);
      await vaultsourcegroupService.deleteVaultSourceGroup(id);
      const targetVaultSourceId = vaultsourceid || formData.vaultsourceid;
      if (targetVaultSourceId) {
        navigate(`/admin/vaults/${targetVaultSourceId}`);
      } else {
        navigate('/admin/vaults');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vault source group');
      setLoading(false);
    }
  };

  if (loading && (isEditMode || isViewMode)) {
    return <div className="vaultsourcegroup-form-container">Loading vault source group...</div>;
  }

  return (
    <div className="vaultsourcegroup-form-container">
      {/* Breadcrumb */}
      {(isViewMode || isEditMode) && (vaultSourceName || formData.name) && (
        <div className="breadcrumb">
          <Link to="/admin/vaults" className="breadcrumb-link">
            Vault Systems
          </Link>
          <span className="breadcrumb-separator">/</span>
          {vaultSourceName && formData.vaultsourceid && (
            <>
              <Link to={`/admin/vaults/${formData.vaultsourceid}`} className="breadcrumb-link">
                {vaultSourceName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          <span className="breadcrumb-current">{formData.name}</span>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              disabled={isViewMode}
              rows={3}
              placeholder="Describe what data this group exports"
            />
          </div>

          <div className="form-group">
            <label htmlFor="icon">Icon (Material icon name)</label>
            <input
              type="text"
              id="icon"
              name="icon"
              value={formData.icon || ''}
              onChange={handleChange}
              disabled={isViewMode}
              placeholder="e.g. receipt_long, badge"
            />
            {formData.icon && (
              <span className="material-icons" style={{ marginLeft: '8px', verticalAlign: 'middle', fontSize: '24px' }}>
                {formData.icon}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="data_type_key">Data Type Key</label>
            <input
              type="text"
              id="data_type_key"
              name="data_type_key"
              value={formData.data_type_key || ''}
              onChange={handleChange}
              disabled={isViewMode}
              placeholder="e.g. folios, eregcard"
            />
          </div>

        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {isViewMode ? (
            <>
              <button type="button" onClick={() => navigate(`/admin/vaults/${vaultsourceid}/vaultsourcegroup/${id}/edit`)} title="Edit Vault System Group">
                ✏️ Edit
              </button>
              <button type="button" onClick={handleDelete} className="btn-danger" title="Delete Vault System Group">
                🗑️ Delete
              </button>
              <button type="button" onClick={handleCancel} title="Back to Vault System">
                ← Back to Vault System
              </button>
            </>
          ) : (
            <>
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isCreateMode ? 'Create Vault System Group' : 'Update Vault System Group'}
              </button>
              <button type="button" onClick={handleCancel} disabled={loading} title="Cancel">
                ← Cancel
              </button>
            </>
          )}
        </div>
      </form>

      {/* Tabs Section - Only show in view/edit mode */}
      {(isViewMode || isEditMode) && id && (
        <div className="tabs-container">
          {/* Tab Navigation */}
          <div className="tabs-nav">
            <button
              className={`tab-button ${activeTab === 'vaulttables' ? 'active' : ''}`}
              onClick={() => setActiveTab('vaulttables')}
            >
              Vault System Tables
            </button>
            <button
              className={`tab-button ${activeTab === 'vaultsql' ? 'active' : ''}`}
              onClick={() => setActiveTab('vaultsql')}
            >
              Vault SQL
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Vault System Tables Tab */}
            {activeTab === 'vaulttables' && (
              <div className="vaulttables-section">
                {/* Add Vault System Tables - Only in view mode */}
                {isViewMode && (
                  <div className="add-tables">
                    <h4>Add Vault System Tables</h4>
                    <div className="search-container">
                      <input
                        type="text"
                        placeholder="Search vault system tables..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                      />
                    </div>

                    {(() => {
                      // Filter available tables by search term
                      const filteredTables = availableVaultTables.filter((table) =>
                        table.name.toLowerCase().includes(searchTerm.toLowerCase())
                      );

                      // Exclude already associated tables
                      const associatedIds = associatedVaultTables.map(t => t.vaulttableid);
                      const unassociatedTables = filteredTables.filter(
                        table => !associatedIds.includes(table.id)
                      );

                      if (searchTerm && unassociatedTables.length === 0) {
                        return <p className="no-results">No matching vault system tables found.</p>;
                      }

                      if (!searchTerm) {
                        return <p className="search-prompt">Start typing to search for vault system tables...</p>;
                      }

                      return (
                        <>
                          <div className="search-results">
                            {unassociatedTables.map((table) => (
                              <div
                                key={table.id}
                                className={`table-checkbox-item ${selectedTableIds.includes(table.id) ? 'selected' : ''}`}
                                onClick={() => handleToggleTableSelection(table.id)}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedTableIds.includes(table.id)}
                                  onChange={() => {}}
                                />
                                <span className="table-checkbox-name">{table.name}</span>
                              </div>
                            ))}
                          </div>

                          {selectedTableIds.length > 0 && (
                            <div className="selected-count">
                              {selectedTableIds.length} table{selectedTableIds.length !== 1 ? 's' : ''} selected
                            </div>
                          )}

                          <button
                            onClick={handleAddSelectedTables}
                            disabled={savingTables || selectedTableIds.length === 0}
                            className="btn-add-tables"
                          >
                            {savingTables ? 'Adding...' : `Add Selected Tables (${selectedTableIds.length})`}
                          </button>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Associated Vault System Tables */}
                <div className="associated-tables">
                  <h4>Associated Vault System Tables ({associatedVaultTables.length})</h4>
                  <div className="table-container">
                    <table className="associated-tables-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          {isViewMode && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {associatedVaultTables.length === 0 ? (
                          <tr>
                            <td colSpan={isViewMode ? "2" : "1"} className="no-data">No vault system tables associated with this group yet.</td>
                          </tr>
                        ) : (
                          associatedVaultTables.map((table) => (
                            <tr key={table.junction_id} className="clickable-row">
                              <td className="table-name-cell">
                                <Link
                                  to={`/admin/vaults/${vaultsourceid}/vaulttable/${table.id}`}
                                  className="table-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {table.name}
                                </Link>
                              </td>
                              {isViewMode && (
                                <td className="actions-cell">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveVaultTable(table.junction_id);
                                    }}
                                    className="btn-action btn-delete-action"
                                    title="Remove from group"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <path d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M4 4h8l-.5 9a1 1 0 01-1 1h-5a1 1 0 01-1-1L4 4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Vault SQL Tab */}
            {activeTab === 'vaultsql' && (
              <div className="vaultsql-section">
                <div className="vaultsql-header">
                  {!showSqlForm && (
                    <button onClick={handleCreateSql} className="btn-add-sql" title="Add Vault SQL">
                      ➕ Add Vault SQL
                    </button>
                  )}
                </div>

                {showSqlForm ? (
                  <div className="sql-form">
                    <h4>{editingSqlId ? 'Edit Vault SQL' : 'Create New Vault SQL'}</h4>
                    <form onSubmit={handleSaveSql}>
                      <div className="form-group">
                        <label htmlFor="sql-name">Name *</label>
                        <input
                          type="text"
                          id="sql-name"
                          name="name"
                          value={sqlFormData.name}
                          onChange={handleSqlFormChange}
                          required
                        />
                      </div>

                      {/* Parameters Section */}
                      <div className="parameters-section">
                        <div className="parameters-header">
                          <h4>Parameters</h4>
                          {!showParameterForm && (
                            <button
                              type="button"
                              onClick={handleAddParameter}
                              className="btn-add-parameter"
                              title="Add Parameter"
                            >
                              ➕ Add Parameter
                            </button>
                          )}
                        </div>

                          {showParameterForm ? (
                            <div className="parameter-form">
                              <h5>{editingParameterId ? 'Edit Parameter' : 'Add New Parameter'}</h5>
                              <div className="parameter-form-fields">
                                <div className="form-group">
                                  <label htmlFor="param-table">Select Table</label>
                                  <select
                                    id="param-table"
                                    value={selectedTableId}
                                    onChange={handleTableSelect}
                                    className="table-select"
                                  >
                                    <option value="">-- Select a table --</option>
                                    {tablesWithColumns.map((table) => (
                                      <option key={table.id} value={table.id}>
                                        {table.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="form-group">
                                  <label htmlFor="param-column">Select Column</label>
                                  <select
                                    id="param-column"
                                    value={selectedColumnId}
                                    onChange={handleColumnSelect}
                                    className="column-select"
                                    disabled={!selectedTableId}
                                  >
                                    <option value="">-- Select a column --</option>
                                    {selectedTableId && tablesWithColumns
                                      .find(t => t.id === parseInt(selectedTableId))
                                      ?.columns.map((column) => (
                                        <option key={column.id} value={column.id}>
                                          {column.name} ({column.data_type})
                                        </option>
                                      ))}
                                  </select>
                                </div>

                                <div className="form-group">
                                  <label htmlFor="param-name">Parameter Name *</label>
                                  <input
                                    type="text"
                                    id="param-name"
                                    name="name"
                                    value={parameterFormData.name}
                                    onChange={handleParameterFormChange}
                                    required
                                    placeholder="Auto-filled when column is selected, or enter manually"
                                  />
                                </div>
                                <div className="form-group">
                                  <label htmlFor="param-value">Default Value</label>
                                  <input
                                    type="text"
                                    id="param-value"
                                    name="value"
                                    value={parameterFormData.value}
                                    onChange={handleParameterFormChange}
                                    placeholder="Optional default value"
                                  />
                                </div>
                                <div className="form-group">
                                  <label htmlFor="param-data-type">Data Type</label>
                                  <select
                                    id="param-data-type"
                                    name="data_type"
                                    value={parameterFormData.data_type}
                                    onChange={handleParameterFormChange}
                                  >
                                    <option value="">Text (default)</option>
                                    <option value="date">Date</option>
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label htmlFor="param-description">Description</label>
                                  <textarea
                                    id="param-description"
                                    name="description"
                                    value={parameterFormData.description}
                                    onChange={handleParameterFormChange}
                                    rows={3}
                                    placeholder="Optional description"
                                  />
                                </div>
                              </div>
                              <div className="parameter-form-actions">
                                <button type="button" onClick={handleSaveParameter} className="btn-save-parameter">
                                  {editingParameterId ? 'Update Parameter' : 'Add Parameter'}
                                </button>
                                <button type="button" onClick={handleCancelParameterForm} className="btn-cancel-parameter">
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="parameters-list">
                              {parameters.length === 0 ? (
                                <p className="no-parameters">No parameters defined. Click "Add Parameter" to create one.</p>
                              ) : (
                                <div className="parameter-items">
                                  {parameters.map((param) => (
                                    <div key={param.id} className="parameter-item">
                                      <div className="parameter-item-header">
                                        <strong className="parameter-name">{param.name}</strong>
                                        <div className="parameter-item-actions">
                                          <button
                                            type="button"
                                            onClick={() => handleEditParameter(param)}
                                            className="btn-edit-parameter"
                                            title="Edit"
                                          >
                                            ✏️
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteParameter(param.id)}
                                            className="btn-delete-parameter"
                                            title="Delete"
                                          >
                                            🗑️
                                          </button>
                                        </div>
                                      </div>
                                      {param.value && (
                                        <div className="parameter-value">Value: {param.value}</div>
                                      )}
                                      {param.description && (
                                        <div className="parameter-description">{param.description}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="sql-canonical-template">Canonical Template</label>
                        <select
                          id="sql-canonical-template"
                          name="canonical_template_id"
                          value={sqlFormData.canonical_template_id || ''}
                          onChange={handleSqlFormChange}
                        >
                          <option value="">— None —</option>
                          {allTemplates.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="sql-query">SQL Query</label>
                        <textarea
                          id="sql-query"
                          name="sql_source"
                          value={sqlFormData.sql_source}
                          onChange={handleSqlFormChange}
                          rows={15}
                          placeholder="Enter SQL query..."
                          style={{ fontFamily: 'monospace' }}
                        />
                      </div>
                      <div className="form-actions">
                        <button type="submit">
                          {editingSqlId ? 'Update Vault SQL' : 'Save Vault SQL'}
                        </button>
                        {editingSqlId && (
                          <button type="button" onClick={handleCancelSqlForm}>
                            Close
                          </button>
                        )}
                        {!editingSqlId && (
                          <button type="button" onClick={handleCancelSqlForm}>
                            Cancel
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="vaultsql-list">
                    <div className="table-container">
                      <table className="vaultsql-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>SQL Preview</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vaultSqls.length === 0 ? (
                            <tr>
                              <td colSpan="3" className="no-data">No vault SQLs found. Click "Add Vault SQL" to create one.</td>
                            </tr>
                          ) : (
                            vaultSqls.map((vaultsql) => (
                              <tr
                                key={vaultsql.id}
                                className="clickable-row"
                                onClick={() => handleEditSql(vaultsql)}
                                title="Click to edit"
                              >
                                <td className="table-name-cell">{vaultsql.name}</td>
                                <td className="sql-preview-cell">
                                  {vaultsql.sql_source ? (
                                    <pre className="sql-preview-text">{vaultsql.sql_source.substring(0, 100)}{vaultsql.sql_source.length > 100 ? '...' : ''}</pre>
                                  ) : (
                                    <span className="no-sql-text">No SQL</span>
                                  )}
                                </td>
                                <td className="actions-cell">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditSql(vaultsql);
                                    }}
                                    className="btn-action btn-edit-action"
                                    title="Edit"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M9.5 4.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSql(vaultsql.id);
                                    }}
                                    className="btn-action btn-delete-action"
                                    title="Delete"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <path d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M4 4h8l-.5 9a1 1 0 01-1 1h-5a1 1 0 01-1-1L4 4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VaultSourceGroupForm;
